import { type BaseOptionType } from "antd/lib/select"
import { type TFunction } from "i18next"

import {
  LEAD_STATUSES_MAP,
  type LeadStatusesValues
} from "../../../../Lead/constants/lead-statuses.ts"
import { OPERATIONAL_STATE_LABELS } from "../../../../Lead/constants/operational-states.ts"
import { LEADS_FILTER_KEYS } from "../../../redux/leads.constants.ts"
import type {
  LeadsFilterOptions,
  LeadsFilters
} from "../../../redux/leads.types.ts"

interface ActiveFilterChip {
  key: keyof LeadsFilters
  label: string
  cleared: Partial<LeadsFilters>
}

type OptionSets = Partial<LeadsFilterOptions>

/** Which `filterOptions` list, if any, resolves a stored value to its label. */
const OPTION_SOURCE: Partial<Record<keyof LeadsFilters, string>> = {
  sales_manager: "sales_manager",
  follow_up_owner: "sales_manager",
  source: "source",
  state: "state",
  project_type: "project_type",
  preferred_contact_method: "contact_method"
}

/** Narrowings that read as a statement rather than a field and a value. */
const FLAG_KEYS: (keyof LeadsFilters)[] = ["show_converted", "is_closed_lost"]

/**
 * A filter is narrowing only when it holds something. `false` is the absence
 * of a flag rather than a flag, and a cleared text input arrives as `""` —
 * both used to render a chip that named a filter nobody had applied.
 */
const isNarrowing = (value: unknown) => {
  if (value === null || value === undefined || value === "") return false
  if (Array.isArray(value)) return value.length > 0
  if (typeof value === "boolean") return value
  return true
}

const resolveOne = (
  key: keyof LeadsFilters,
  entry: unknown,
  filterOptions?: OptionSets
): string => {
  // Both of these have a client-side label map, so they read correctly even
  // before `filter_options` has loaded.
  if (key === "operational_states") {
    return (
      OPERATIONAL_STATE_LABELS[
        entry as keyof typeof OPERATIONAL_STATE_LABELS
      ] ?? String(entry)
    )
  }

  if (key === "status") {
    return (
      LEAD_STATUSES_MAP[Number(entry) as LeadStatusesValues]?.label ??
      String(entry)
    )
  }

  const source = OPTION_SOURCE[key]
  const options = source
    ? (filterOptions as Record<string, BaseOptionType[] | undefined>)?.[source]
    : undefined
  const match = options?.find(
    (option) => String(option?.value) === String(entry)
  )

  return match?.label != null ? String(match.label) : String(entry)
}

const describeValue = (
  key: keyof LeadsFilters,
  value: unknown,
  filterOptions?: OptionSets
): string =>
  (Array.isArray(value) ? value : [value])
    .map((entry) => resolveOne(key, entry, filterOptions))
    .join(", ")

/**
 * One chip per narrowing currently applied, in the order `LEADS_FILTER_KEYS`
 * declares them so the row does not reshuffle as filters come and go.
 */
const activeFilterChips = (
  filters: LeadsFilters,
  t: TFunction,
  filterOptions?: OptionSets
): ActiveFilterChip[] =>
  LEADS_FILTER_KEYS.filter((key) => isNarrowing(filters[key])).map((key) => ({
    key,
    label: FLAG_KEYS.includes(key)
      ? t(`leadsTable.activeFilters.${key}`)
      : `${t(`leadsTable.activeFilters.${key}`)}: ${describeValue(key, filters[key], filterOptions)}`,
    cleared: {
      [key]: Array.isArray(filters[key]) ? [] : null
    } as Partial<LeadsFilters>
  }))

export { type ActiveFilterChip, activeFilterChips }
